import { FormEvent, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { adminApi } from "../api/orgApi";
import type { Org } from "../api/orgApi";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomButton from "../components/CustomButton";

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

type SettingRow = { key: string; value: string };

function orgSettingsToRows(settings: Record<string, unknown> | null): SettingRow[] {
  return Object.entries(settings ?? {}).map(([key, value]) => ({ key, value: String(value) }));
}

function OrgSettingsEditor({ org, onSaved }: { org: Org; onSaved: (updated: Org) => void }) {
  const [existingRows, setExistingRows] = useState<SettingRow[]>(() => orgSettingsToRows(org.settings));
  const [deletingKeys, setDeletingKeys] = useState<Set<string>>(new Set());
  const [draftRows, setDraftRows] = useState<SettingRow[]>([]);
  const [allowedKeys, setAllowedKeys] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    adminApi.getAllowedSettingKeys().then(setAllowedKeys).catch(() => {});
  }, []);

  const usedKeys = new Set([...existingRows.map((r) => r.key), ...draftRows.map((r) => r.key)]);
  const availableKeys = allowedKeys.filter((k) => !usedKeys.has(k));

  function updateExistingValue(key: string, value: string) {
    setExistingRows((prev) => prev.map((r) => (r.key === key ? { ...r, value } : r)));
  }
  function updateDraftRow(i: number, field: "key" | "value", value: string) {
    setDraftRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  }
  function addDraftRow() {
    if (availableKeys.length === 0) return;
    setDraftRows((prev) => [...prev, { key: availableKeys[0], value: "" }]);
  }
  function discardDraftRow(i: number) {
    setDraftRows((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function onDelete(key: string) {
    setDeletingKeys((prev) => new Set(prev).add(key));
    setErr(null);
    try {
      const updated = await adminApi.deleteOrgSetting(org.id, key);
      setExistingRows(orgSettingsToRows(updated.settings));
      onSaved(updated);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setDeletingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }

  async function onSave() {
    setBusy(true);
    setErr(null);
    try {
      const settings: Record<string, string> = {};
      for (const row of existingRows) settings[row.key] = row.value;
      for (const row of draftRows) {
        if (row.key.trim()) settings[row.key.trim()] = row.value;
      }
      const updated = await adminApi.updateOrgSettings(org.id, settings);
      setDraftRows([]);
      setExistingRows(orgSettingsToRows(updated.settings));
      onSaved(updated);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      {existingRows.length === 0 && draftRows.length === 0 && (
        <Typography variant="body2" className="login-subtitle">No settings yet — add one below.</Typography>
      )}
      {existingRows.map((row) => {
        const isDeleting = deletingKeys.has(row.key);
        return (
          <Box key={row.key} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField label="Key" size="small" value={row.key} disabled sx={{ flex: 1 }} />
            <TextField
              label="Value"
              size="small"
              type="password"
              value={row.value}
              onChange={(e) => updateExistingValue(row.key, e.target.value)}
              sx={{ flex: 2 }}
            />
            <IconButton size="small" onClick={() => onDelete(row.key)} disabled={isDeleting} aria-label={`Delete ${row.key}`}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
      {draftRows.map((row, i) => {
        const keyOptions = row.key && !availableKeys.includes(row.key) ? [row.key, ...availableKeys] : availableKeys;
        return (
          <Box key={`draft-${i}`} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                value={row.key}
                onChange={(e: SelectChangeEvent) => updateDraftRow(i, "key", e.target.value)}
                displayEmpty
                aria-label="Setting key"
              >
                {keyOptions.map((k) => (
                  <MenuItem key={k} value={k}>{k}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Value"
              size="small"
              type="password"
              value={row.value}
              onChange={(e) => updateDraftRow(i, "value", e.target.value)}
              sx={{ flex: 2 }}
            />
            <IconButton size="small" onClick={() => discardDraftRow(i)} aria-label="Discard new setting">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
      <Box sx={{ display: "flex", gap: 1 }}>
        <CustomButton
          loading={false}
          disabled={availableKeys.length === 0}
          buttonText="+ Add setting"
          buttonTextLoading=""
          handleSubmitClick={addDraftRow}
          background="transparent"
          color="rgba(74, 30, 71, 1)"
          border="1px solid rgba(74, 30, 71, 0.70)"
          hoverBgColor="rgba(74, 30, 71, 0.08)"
        />
        <CustomButton loading={busy} buttonText="Save settings" buttonTextLoading="Saving…" handleSubmitClick={onSave} />
      </Box>
      {err && <Alert severity="error">{err}</Alert>}
    </Box>
  );
}

export function AdminPage() {
  const { isAdmin } = useAuth();

  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [orgSlug, setOrgSlug] = useState("");
  const [orgDesc, setOrgDesc] = useState("");
  const [createBusy, setCreateBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        setOrgs(await adminApi.listOrgs());
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!orgName.trim() || !orgSlug.trim()) return;
    setCreateBusy(true);
    setErr(null);
    try {
      const org = await adminApi.createOrg(orgName.trim(), orgSlug.trim(), orgDesc.trim() || undefined);
      setOrgs((prev) => [org, ...prev]);
      setOrgName("");
      setOrgSlug("");
      setOrgDesc("");
      setShowCreate(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setCreateBusy(false);
    }
  }

  if (!isAdmin) {
    return (
      <Box className="orgs-page">
        <Alert severity="error">You don't have access to the Admin Panel.</Alert>
      </Box>
    );
  }

  return (
    <Box className="orgs-page">
      <Box className="orgs-header">
        <Box>
          <Typography variant="h5" className="orgs-page-title">Admin Panel</Typography>
          <Typography variant="body2" className="login-subtitle">
            Create organizations and manage their settings.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        {!showCreate && (
          <CustomButton
            loading={false}
            buttonText="+ New organization"
            buttonTextLoading=""
            handleSubmitClick={() => setShowCreate((v) => !v)}
          />
        )}
      </Box>

      {showCreate && (
        <Card className="orgs-create-card" sx={{ mb: 3 }}>
          <CardContent component="form" onSubmit={onCreate} className="login-card-content">
            <Typography variant="h6" className="login-title">Create organization</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Name"
                  value={orgName}
                  onChange={(e) => { setOrgName(e.target.value); setOrgSlug(slugify(e.target.value)); }}
                  placeholder="Acme Corp"
                  size="small"
                  fullWidth
                  autoFocus
                  className="login-input"
                />
                <TextField
                  label="Slug (URL-safe)"
                  value={orgSlug}
                  onChange={(e) => setOrgSlug(slugify(e.target.value))}
                  placeholder="acme-corp"
                  size="small"
                  fullWidth
                  className="login-input"
                />
              </Box>
              <TextField
                label="Description (optional)"
                value={orgDesc}
                onChange={(e) => setOrgDesc(e.target.value)}
                placeholder="Short description"
                size="small"
                fullWidth
                className="login-input"
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
              <CustomButton
                loading={false}
                buttonText="Cancel"
                buttonTextLoading=""
                handleSubmitClick={() => setShowCreate(false)}
                background="transparent"
                color="rgba(74, 30, 71, 1)"
                border="1px solid rgba(74, 30, 71, 0.70)"
                hoverBgColor="rgba(74, 30, 71, 0.08)"
              />
              <CustomButton
                loading={createBusy}
                disabled={!orgName.trim() || !orgSlug.trim()}
                buttonText="Create organization"
                buttonTextLoading="Creating…"
                handleSubmitClick={() => {}}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {loading ? (
        <Typography variant="body2" className="login-subtitle">Loading…</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {orgs.map((org) => (
            <Card key={org.id} className="orgs-create-card">
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                  onClick={() => setExpandedOrgId((cur) => (cur === org.id ? null : org.id))}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{org.name}</Typography>
                    <Typography variant="caption" sx={{ color: "rgba(120,120,125,1)" }}>
                      <code className="orgs-slug">{org.slug}</code>
                      {org.description ? ` — ${org.description}` : ""}
                    </Typography>
                  </Box>
                  <Typography variant="body2" className="login-subtitle">
                    {expandedOrgId === org.id ? "Hide settings ▲" : "Settings ▼"}
                  </Typography>
                </Box>
                {expandedOrgId === org.id && (
                  <OrgSettingsEditor
                    org={org}
                    onSaved={(updated) => setOrgs((prev) => prev.map((o) => (o.id === updated.id ? updated : o)))}
                  />
                )}
              </CardContent>
            </Card>
          ))}
          {orgs.length === 0 && (
            <Typography variant="body2" className="login-subtitle">No organizations yet.</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
